import React, { useEffect, useMemo, useState } from 'react';
import { Tabs } from 'antd';
import { Maths } from 'helpers/maths';
import { useStore } from 'components/Store';
import { hasAccess } from 'utilities/access';
import usePlanData from 'screens/Plans/Plan/hooks/usePlanData';
import useTagsData from 'hooks/useTagsData';
import useCropData from 'hooks/useCropData';
import useProduct from 'hooks/useProduct';
import useActiveTab from 'screens/Applications&WorkOrders/Components/hooks/useActiveTab';
import DetailPanel from 'components/RecordsListView/DetailPanel';
import { OperationType } from 'components/RecordsListView/constants';
import {
  getPlanFixedCost,
  getVarCost,
  getPlanProjNetRevenue,
  getYieldTotal,
  getYieldTotalPerAc
} from 'screens/Plans/components/PlanView/helpers';
import { Plan } from '../../ConvertPlanModalV2/types';
import { PlanDetails, perAcreOfTotal } from './PlanDetailPanel.helpers';
import PlanActionMenu from './PlanActionMenu';
import PlanProductsTab from './PlanProductsTab';
import PlanPropertiesTab from './PlanPropertiesTab';
import PlanDetailHeaderCards from './PlanDetailHeaderCards';
import PlanDetailsTab from './PlanDetailsTab';
import PlanFixedCostTab from './PlanFixedCostTab';
import PlanProjectedRevenueTab from './PlanProjectedRevenueTab';
import PlanNotesTab from './PlanNotesTab';
// Card-tab styling shared with the Applications/Work Orders and Invoices detail views.
import 'screens/Applications&WorkOrders/Components/DetailsTabs/index.css';
import 'screens/Applications&WorkOrders/Components/DetailsTabs/components/DetailsTab/DetailsTab.css';
import 'screens/Applications&WorkOrders/Components/DetailsTabs/components/ProductsTab/index.css';
import 'screens/Invoices/redesign/components/InvoiceDetailPanel.css';
import './PlanDetailPanel.css';

export interface PlanDetailPanelProps {
  planId: string | null;
  onPlanLoaded?: (plan: PlanDetails) => void;
  /** List-level loading state from `usePlans()` — kept true while the Plans list itself is still being fetched, before a row can be auto-selected (mirrors `InvoiceDetailPanel`/`OperationDetailPanel`'s own `isLoading` prop). */
  isLoading?: boolean;
  /** Fired after a successful delete so the list can re-select + reload. */
  onPlanDeleted?: (planId: string) => void;
  /** FR-065: true when Plans' own search state (not the inert `SummaryParamsProvider`) has narrowed the list to zero results. */
  showSearchEmptyState?: boolean;
  /** FR-065: true when Plans' own Creation Date filter has narrowed the list to zero results. */
  showFilterEmptyState?: boolean;
  /** Opens Plans' real filter drawer — overrides `DetailPanel`'s default, which targets the inert `SummaryParamsProvider` Plans only wraps to satisfy that shared component's context requirement. */
  onAdjustFilters?: () => void;
  /** Resets Plans' real filter state — same reason as `onAdjustFilters`. */
  onClearFilters?: () => void;
}

type DetailTab =
  | 'products'
  | 'plannedArea'
  | 'details'
  | 'fixedCost'
  | 'projectedRevenue'
  | 'notes';

/**
 * Inline master-detail panel for the Plans list (FR-010/011), wrapping the
 * shared `<DetailPanel>` shell with card-style Tabs (Details/Fixed Cost/
 * Projected Revenue/Properties/Products/Notes), mirroring `InvoiceDetailPanel`.
 *
 * Each tab's content and the header cards are extracted into sibling
 * components (`PlanDetailsTab`, `PlanFixedCostTab`, `PlanProjectedRevenueTab`,
 * `PlanNotesTab`, `PlanDetailHeaderCards`) — this component's job is just
 * resolving the Plan + its related data and wiring it into them.
 *
 * `PlanPropertiesTab` replaces the legacy `PropertiesTable`/`PropertyTableRow`
 * (dead `handleSort`, one `usePopulateMapData` fetch per row) with a single
 * tab-level fetch resolved via `getPlanProperties`.
 */
const PlanDetailPanel: React.FC<PlanDetailPanelProps> = ({
  planId,
  onPlanLoaded,
  isLoading: isListLoading = false,
  onPlanDeleted,
  showSearchEmptyState = false,
  showFilterEmptyState = false,
  onAdjustFilters,
  onClearFilters
}) => {
  // The default active tab is 'products', not 'details' — intentionally
  // doesn't match the declared tab order below. Persisted via sessionStorage
  // so a refresh keeps the last-selected tab instead of resetting.
  const { activeTab, setActiveTab } = useActiveTab(
    'plan-details',
    'products'
  ) as { activeTab: DetailTab; setActiveTab: (tab: DetailTab) => void };
  const {
    state: { loggedInUserOrgPermission, cropSeasons: allCropSeasons }
  } = useStore();
  // `usePlanData` treats id === 'plan' as create-mode (no fetch, plan: {}) —
  // reused here as the "nothing selected" placeholder id rather than adding a
  // second no-op branch to that hook.
  const { plan, loading } = usePlanData(planId ?? 'plan') as {
    plan: PlanDetails;
    loading: boolean;
  };

  useEffect(() => {
    if (plan?.id && plan.name) onPlanLoaded?.(plan);
  }, [plan, onPlanLoaded]);
  // Crop seasons come from the Store rather than a fresh `useCropSeasons()`
  // call: that hook refetches on every mount, and the parent's loading gate
  // was remounting this panel while a fetch was in flight, forming a fetch
  // loop that never settled.
  const { tags: allTags } = useTagsData();
  const { crops: allCrops } = useCropData() as {
    crops?: Array<{ id: string; name: string }>;
  };

  const cropZones = plan?.cropZones ?? [];
  // A fetched Plan's own `coveragePercent` is persisted as a 0-1 fraction
  // (matches `planFormMapping.ts`'s `mapFetchedProducts`, which applies this
  // same `* 100` conversion for the wizard's edit-mode Formik scale) — this
  // read-only view has no equivalent conversion of its own, so without this
  // it renders e.g. "1.00%" instead of "100.00%".
  const products = useMemo(
    () =>
      (plan?.planProductEntries ?? []).map(product => ({
        ...product,
        coveragePercent: Maths.multiplyRound(
          product.coveragePercent ?? 1,
          100,
          2
        )
      })),
    [plan]
  );
  const isEmpty = !planId || (!loading && !plan?.id);

  // `producttype`/`registrationnumber`/`stdunit` are UI-only fields, stripped
  // before save (see `usePlanWizardMutations.ts`'s `buildPlanProductEntriesPayload`)
  // and never guaranteed to round-trip — a fetched Plan's own product entries
  // don't carry them. Backfills the same way `ProductsStep.tsx`'s wizard
  // already does for its own "already selected" catalog lookup, so the
  // Products tab's "View More" drawer (FR-082) shows real values instead of
  // "---"/blank.
  interface CatalogDetails {
    productType?: string;
    stdunit?: string;
    stdpackageunit?: string;
    registrationNumber?: string;
  }
  const { fetchProductData } = useProduct();
  const [catalogDetailsByProductId, setCatalogDetailsByProductId] = useState<
    Record<string, CatalogDetails>
  >({});

  useEffect(() => {
    const missing = products.filter(
      p => !catalogDetailsByProductId[p.productId]
    );
    if (!missing.length) return undefined;

    let cancelled = false;

    Promise.all(
      missing.map(p =>
        fetchProductData({ productId: p.productId, custom: p.custom }).then(
          (result: { data?: CatalogDetails } | null) => ({
            productId: p.productId,
            data: result?.data
          })
        )
      )
    ).then(results => {
      if (cancelled) return;
      const additions: Record<string, CatalogDetails> = {};
      results.forEach(({ productId, data }) => {
        if (data) additions[productId] = data;
      });
      setCatalogDetailsByProductId(prev => ({ ...prev, ...additions }));
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const productsWithCatalogDetails = useMemo(
    () =>
      products.map(product => {
        const details = catalogDetailsByProductId[product.productId];
        return {
          ...product,
          producttype: product.producttype || details?.productType || '',
          registrationnumber:
            product.registrationnumber || details?.registrationNumber || '',
          stdunit: product.stdunit || details?.stdunit || '',
          stdpackageunit:
            product.stdpackageunit || details?.stdpackageunit || ''
        };
      }),
    [products, catalogDetailsByProductId]
  );

  // Matches `PlanView/index.jsx`'s `getDisplayByFinancialAccess` masking rule
  // and reuses its cost/revenue math (`PlanView/helpers.js`) verbatim so the
  // figures shown here match the legacy single-page view exactly.
  const displayByFinancialAccess = hasAccess(loggedInUserOrgPermission, {
    minAllowedRole: 'View Only',
    minFinancialAccess: 'READ'
  });
  const fixedCostTotal = getPlanFixedCost(plan) || 0;
  const variableCostTotal = getVarCost(plan) || 0;
  const netRevenueTotal = getPlanProjNetRevenue(plan) || 0;

  const cropName = allCrops?.find(crop => crop.id === plan?.cropId)?.name;

  const cropSeasonNames = (plan?.cropSeasonIds ?? [])
    .map(
      id =>
        (allCropSeasons as
          | Array<{ id: string; name: string }>
          | undefined)?.find(season => season.id === id)?.name
    )
    .filter(Boolean) as string[];

  // `getYieldTotal`/`getYieldTotalPerAc` (PlanView/helpers.js) actually
  // compute a dollar revenue figure (yieldPerArea × pricePerUnit × area),
  // despite the name — reused below as "Yield Revenue" (FR-056), not
  // "Yield Total". "Yield Total" itself is the raw quantity (yieldPerArea ×
  // area, no price), which this file computes locally since no shared
  // helper exists for it.
  const yieldPerAcre = plan?.projectedRevenue?.yieldPerArea ?? 0;
  const pricePerUnit = plan?.projectedRevenue?.pricePerUnit ?? 0;
  const yieldQuantityTotal = Maths.multiplyRound(
    yieldPerAcre,
    plan?.estimatedAreaValue ?? 0,
    2
  );
  const yieldRevenueTotal = getYieldTotal(plan);
  const yieldRevenuePerAcre = getYieldTotalPerAc(plan);
  const fsaPaymentTotal = plan?.projectedRevenue?.fsaPayment ?? 0;
  const fsaPaymentPerAcre = perAcreOfTotal(
    fsaPaymentTotal,
    plan?.estimatedAreaValue
  );
  // Crop Share renders as a percentage, not a computed dollar amount — design
  // feedback flagged the dollar value as showing Total Revenue instead.
  // `cropSharePercent` is a 0-1 fraction, defaulting to 1 (100%) when missing,
  // matching `planFormMapping.ts`'s own default for this field.
  const rawCropSharePercent = plan?.projectedRevenue?.cropSharePercent;
  const cropSharePercentValue = Maths.multiplyRound(
    rawCropSharePercent === '' || rawCropSharePercent == null
      ? 1
      : rawCropSharePercent,
    100,
    2
  );
  const totalCostTotal = Maths.plusRound(fixedCostTotal, variableCostTotal, 2);
  const costPerAcre = perAcreOfTotal(totalCostTotal, plan?.estimatedAreaValue);

  return (
    <DetailPanel
      title={plan?.name ?? ''}
      operationType={OperationType.PLAN}
      isLoading={isListLoading || loading}
      isEmpty={isEmpty}
      showSearchEmptyState={showSearchEmptyState}
      showFilterEmptyState={showFilterEmptyState}
      onAdjustFilters={onAdjustFilters}
      onClearFilters={onClearFilters}
      testId="plan-detail-panel"
      actionMenu={
        plan?.id ? (
          <PlanActionMenu
            plan={(plan as unknown) as Plan}
            onPlanDeleted={onPlanDeleted}
          />
        ) : (
          undefined
        )
      }
    >
      {/* FR-055: two 50/50 cards — Total/Fixed/Variable Cost, and
          Projected Net Revenue/Cost per Acre. Planned Area lives in its
          own tab (FR-054). */}
      <PlanDetailHeaderCards
        estimatedAreaUnit={plan?.estimatedAreaUnit}
        totalCostTotal={totalCostTotal}
        fixedCostTotal={fixedCostTotal}
        variableCostTotal={variableCostTotal}
        netRevenueTotal={netRevenueTotal}
        costPerAcre={costPerAcre}
        displayByFinancialAccess={displayByFinancialAccess}
      />

      <div className="operation-details-tabs">
        <Tabs
          activeKey={activeTab}
          onChange={key => setActiveTab(key as DetailTab)}
          type="card"
          className="leading-5"
          items={[
            {
              key: 'products',
              label: 'Products',
              forceRender: true,
              children: (
                <div data-testid="plan-detail-tab-panel-products">
                  <PlanProductsTab products={productsWithCatalogDetails} />
                </div>
              )
            },
            {
              key: 'plannedArea',
              label: 'Planned Area',
              forceRender: true,
              children: (
                <div data-testid="plan-detail-tab-panel-planned-area">
                  <PlanPropertiesTab
                    cropZones={cropZones}
                    cropSeasonNames={cropSeasonNames.join(', ')}
                  />
                </div>
              )
            },
            {
              key: 'details',
              label: 'Details',
              forceRender: true,
              children: (
                <PlanDetailsTab
                  plan={plan}
                  cropName={cropName}
                  cropSeasonNames={cropSeasonNames}
                  allTags={allTags}
                />
              )
            },
            {
              key: 'fixedCost',
              label: 'Fixed Cost',
              forceRender: true,
              children: (
                <PlanFixedCostTab
                  plan={plan}
                  displayByFinancialAccess={displayByFinancialAccess}
                />
              )
            },
            {
              key: 'projectedRevenue',
              label: 'Projected Revenue',
              forceRender: true,
              children: (
                <PlanProjectedRevenueTab
                  estimatedAreaUnit={plan?.estimatedAreaUnit}
                  yieldQuantityTotal={yieldQuantityTotal}
                  yieldPerAcre={yieldPerAcre}
                  pricePerUnit={pricePerUnit}
                  yieldRevenueTotal={yieldRevenueTotal}
                  yieldRevenuePerAcre={yieldRevenuePerAcre}
                  fsaPaymentTotal={fsaPaymentTotal}
                  fsaPaymentPerAcre={fsaPaymentPerAcre}
                  cropSharePercentValue={cropSharePercentValue}
                  displayByFinancialAccess={displayByFinancialAccess}
                />
              )
            },
            {
              key: 'notes',
              label: 'Notes',
              forceRender: true,
              children: <PlanNotesTab notes={plan?.notes} />
            }
          ]}
        />
      </div>
    </DetailPanel>
  );
};

export default PlanDetailPanel;
